/** Thin client over the Mission Agent read API. One place, so components
 *  never duplicate a fetch. Uses the global axios instance, so Sentio's token
 *  refresh interceptor (UserContext) applies. */

import axios from "axios";

import * as CONSTANTS from "../../utils/constants/constants";
import type {
  ActivityRow,
  CloseoutResponse,
  Dashboard,
  FeedItem,
  MissionListRow,
  SimulationOptions,
  SimulationResponse,
} from "./types";

const BASE = () => `${CONSTANTS.HOST}api/missionagent`;

async function get<T>(token: string, path: string, params?: URLSearchParams): Promise<T> {
  const response = await axios.get<T>(`${BASE()}${path}`, {
    headers: { authorization: `Bearer ${token}` },
    params,
  });
  return response.data;
}

const mission = (missionId: string) => `/missions/${encodeURIComponent(missionId)}`;

/** This browser's time zone ("Europe/Oslo"). Times are stored in UTC; whatever the server writes times into
 *  for this page (drafts, the care record, the ePCR) writes them in this zone. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/** The zone's short name at that moment ("CEST", "GMT+2"): what the page labels times with. */
export function timeZoneLabel(at: Date = new Date()): string {
  try {
    const part = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" })
      .formatToParts(at)
      .find((p) => p.type === "timeZoneName");
    return part?.value ?? browserTimeZone();
  } catch {
    return browserTimeZone();
  }
}

const zoneParams = () => new URLSearchParams({ tz: browserTimeZone() });

export function fetchDeviceMissions(token: string, deviceId: string): Promise<MissionListRow[]> {
  return get(token, `/devices/${encodeURIComponent(deviceId)}/missions/`);
}

export function fetchDashboard(token: string, missionId: string): Promise<Dashboard> {
  return get(token, `${mission(missionId)}/dashboard/`, zoneParams());
}

export function fetchActivity(
  token: string,
  missionId: string,
  kind: "event" | "agent_run",
  limit = 60,
): Promise<ActivityRow[]> {
  return get(token, `${mission(missionId)}/activity/`, new URLSearchParams({ kind, limit: String(limit) }));
}

/** Recent Updates: engine events and drafts as one feed, newest first. */
export function fetchUpdates(token: string, missionId: string, limit = 50): Promise<FeedItem[]> {
  return get(token, `${mission(missionId)}/updates/`, new URLSearchParams({ limit: String(limit) }));
}

/** Where the mission's close-out is (null `closeout` until someone asks). */
export function fetchCloseout(token: string, missionId: string): Promise<CloseoutResponse> {
  return get(token, `${mission(missionId)}/closeout/`);
}

/** Close the mission out (or retry a failed close-out). Answers the close-out
 *  as it stands: an existing one is returned rather than started twice. */
export async function startCloseout(token: string, missionId: string): Promise<CloseoutResponse> {
  const response = await axios.post<CloseoutResponse>(
    `${BASE()}${mission(missionId)}/closeout/`,
    { time_zone: browserTimeZone() },
    { headers: { authorization: `Bearer ${token}` } },
  );
  return response.data;
}

const simulation = (deviceId: string) => `${BASE()}/devices/${encodeURIComponent(deviceId)}/simulation/`;

/** Whether this user may simulate missions on the device here, and its last simulation. */
export function fetchSimulation(token: string, deviceId: string): Promise<SimulationResponse> {
  return get(token, `/devices/${encodeURIComponent(deviceId)}/simulation/`);
}

/** Start a simulated mission on the device. A 409 (one already running) still answers with it. */
export async function startSimulation(
  token: string,
  deviceId: string,
  options: SimulationOptions,
): Promise<SimulationResponse> {
  const response = await axios.post<SimulationResponse>(simulation(deviceId), options, {
    headers: { authorization: `Bearer ${token}` },
    validateStatus: (status) => status === 202 || status === 409,
  });
  return response.data;
}

/** Stop the device's simulation: the device goes quiet and its mission stays open. */
export async function stopSimulation(token: string, deviceId: string): Promise<SimulationResponse> {
  const response = await axios.delete<SimulationResponse>(simulation(deviceId), {
    headers: { authorization: `Bearer ${token}` },
    validateStatus: (status) => status === 202 || status === 409,
  });
  return response.data;
}

/** End the mission (final) and start its ePCR. Answers the close-out as it stands. */
export async function endMission(token: string, missionId: string): Promise<CloseoutResponse> {
  const response = await axios.post<CloseoutResponse>(
    `${BASE()}${mission(missionId)}/end/`,
    { time_zone: browserTimeZone() },
    { headers: { authorization: `Bearer ${token}` } },
  );
  return response.data;
}

/** Download the mission's patient care record (PDF) as a file - once the
 *  mission is closed out, the mission handoff (record + device report). The name is
 *  the API's when the browser may read it (cross-origin it usually may not),
 *  else the same name built here. */
export async function downloadCareRecord(
  token: string,
  missionId: string,
  incident: string,
  closedOut = false,
): Promise<void> {
  let response;
  try {
    response = await axios.get<Blob>(`${BASE()}${mission(missionId)}/care-record.pdf`, {
      headers: { authorization: `Bearer ${token}` },
      params: zoneParams(),
      responseType: "blob",
    });
  } catch (error) {
    // A blob request answers an error as a blob: read the API's `detail` out of it.
    if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
      const detail = await error.response.data
        .text()
        .then((t) => (JSON.parse(t) as { detail?: string }).detail)
        .catch(() => undefined);
      if (detail) throw new Error(detail);
    }
    throw error;
  }
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  // Local time, as the API names it (in the zone the page sends).
  const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
  const prefix = closedOut ? "ePCR" : "patient-care-record";
  const fallback = `${prefix}-${incident.replace(/[^A-Za-z0-9_-]+/g, "-")}-${stamp}.pdf`;
  const disposition = String(response.headers["content-disposition"] ?? "");
  const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallback;

  const url = URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on the next tick: some browsers start the download asynchronously.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** A readable message for a failed request, preferring the API's `detail`. */
export function errorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: string } | undefined)?.detail;
    if (detail) return detail;
    if (error.response) return `${error.response.status}: ${error.response.statusText}`;
  }
  return (error as Error)?.message ?? String(error);
}
